import apiAuthClient from '@/services/apiAuthClient';
import apiClient from '@/services/apiClient';
import { defaultEoi } from '@/utilities/constants';

import errorHandler from '@/utilities/serverErrorHandler';

export const actions = {
  async getEOI ({ commit, dispatch, getters, rootGetters }, { isNewlyCreated = false } = {}) {
    dispatch('ui/updateInterstitial', { isLoading: true, message: 'Getting your application...' }, { root: true });
  
    // If EOI was just created, wait 10 seconds before attempting to fetch
    if (isNewlyCreated) {
      dispatch('ui/updateInterstitial', {
        isLoading: true,
        message: 'Please wait whilst we load your application...'
      }, { root: true });
      
      await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
      
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Retrieving your application...' }, { root: true });
    }
    
    const maxRetries = 5;
    let attempt = 0;
    
    try {
      while (attempt < maxRetries) {
        attempt++;
        
        const res = await apiAuthClient.get(`iceoi/${process.env.VUE_APP_CONTRACT_ID_EOI}`, rootGetters['auth/getAccessToken']);
        
        if (res.status === 200) {
          // Check if the required field/path exists in the data
          if (!res.data.ua_sharepointfoldername) {
            console.log(`Required field not found, attempt ${attempt} of ${maxRetries}`);
            
            if (attempt < maxRetries) {
              dispatch('ui/updateInterstitial', {
                isLoading: true,
                message: 'Please wait whilst we load your application...'
              }, { root: true });
              
              await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
              
              continue; // Try again
            } else {
              // Max retries reached, return error
              dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
              dispatch('settings/updateDownloadedFlag', { flagName: 'eoiDownloaded', flagStatus: false }, { root: true });
              return {
                status: 'generic',
                message: 'We\'re just getting your expression of interest application up to date. Please refresh the page in a couple of minutes.'
              };
            }
          } else {
            // Field exists, proceed normally
            commit('UPDATE_EOI', res.data);
            dispatch('settings/updateDownloadedFlag', { flagName: 'eoiDownloaded', flagStatus: true }, { root: true });
            dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
            return true;
          }
        }
      }
    } catch (err) {
      dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      return errorHandler('eoiErrors', err);
    }
  },
  createEOI ({ commit, getters, rootGetters, dispatch }) {
      dispatch('ui/updateInterstitial', { isLoading: true, message: 'Creating your application...' }, { root: true });
      const eoi = getters.getEOI;
      return apiAuthClient.post('iceoi', eoi, rootGetters['auth/getAccessToken']).then((res) => {
        if (res.status === 200) {
          commit('UPDATE_EOI', res.data.Data);
          dispatch('settings/updateDownloadedFlag', { flagName: 'eoiDownloaded', flagStatus: true }, { root: true });
          return true;
        }
      }).catch((err) => {
        return errorHandler('eoiErrors', err);
      }).finally(() => {
        dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
      });
  },
  updateCurrentStep ({ commit }, payload) {
    commit('UPDATE_CURRENT_STEP', payload);
  },
  updateEOI ({ commit, getters, rootGetters, dispatch }, payload) {
    commit('UPDATE_EOI', payload);
    commit('UPDATE_ADDITIONAL_POSTCODE_FIELDS');

    const eoi = getters.getEOI;

    dispatch('ui/updateInterstitial', { isLoading: true, message: 'Updating your application...' }, { root: true });
    return apiAuthClient.put('iceoi', eoi, rootGetters['auth/getAccessToken']).then((res) => {
      if (res.status === 200) {
        return true;
      }
    }).catch((err) => {
      return errorHandler('eoiErrors', err, 'put');
    }).finally(() => {
      dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
    });
  },
  resetEOI ({ commit }) {
    commit('UPDATE_EOI', defaultEoi);
  },
  checkPostcodeEligibility ({ dispatch }, payload) {
    dispatch('ui/updateInterstitial', { isLoading: true, message: 'Checking your postcode...' }, { root: true });
    return apiClient.get(`api/v1/icfund/postcodes/ispostcodeincontract?postcode=${payload}&contractId=${process.env.VUE_APP_CONTRACT_ID_EOI}`).then((res) => {
      if (res.status === 200) {
        return res.data;
      }
      return false;
    }).catch((err) => {
      console.log(err);
    }).finally(() => {
      dispatch('ui/updateInterstitial', { isLoading: false, message: '' }, { root: true });
    });
  }
};
